import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components/macro";
import Loader from "react-loader-spinner";
import { JobListHeader } from "./job_list_header";
import { JobTableView } from "./job_table_view";
import { JobGroupedView } from "./job_grouped_view";
import { BulkActionBar } from "./bulk_action_bar";
import { JobDetailDrawer } from "./job_detail_drawer";
import { CancelReasonModal } from "./cancel_reason_modal";
import { cancelJob } from "./printer_list_page";
import { getRequest } from "../utils/commHelper";
import { useToast } from "./toast";
import { theme, STATUS_FILTER_GROUPS, ACTIVE_JOB_STATUSES } from "./theme";
import { getJobsLimitForDateRange } from "./printerJobsQuery";

/**
 * Selecting jobs (individually or via bulk-select) never guarantees they're
 * still cancelable by the time "Confirm cancel" is actually pressed — a job
 * can finish printing or fail in the background while sitting selected. This
 * re-checks each id's live status against ACTIVE_JOB_STATUSES right before
 * dispatching any cancel call, so a stale selection can never cancel (and, on
 * the server, potentially refund) a job that has already reached a terminal
 * outcome.
 */
export function getCancelableJobIds(jobIds, jobsById) {
  return jobIds.filter((id) => ACTIVE_JOB_STATUSES.includes(jobsById.get(id)?.status));
}

function usePrevious(value) {
  const ref = useRef(null);
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

// `title`, `dateRange` and `onDateRangeChange` are only passed by the per-printer
// page (printer_list_page.js), where the list is a server-fetched history page
// for one room; the global "Print jobs" section leaves them unset and gets the
// default title and no date controls.
const PrinterJobs = ({
  printers,
  jobs,
  isDev = false,
  loading = false,
  onOpenPrinter,
  title,
  dateRange = null,
  onDateRangeChange,
}) => {
  const [renderColor, setRenderColor] = useState(false);
  const prevProps = usePrevious({ jobs });

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const [sortAsc, setSortAsc] = useState(false);
  const [layout, setLayout] = useState("table");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [drawerJob, setDrawerJob] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null); // {type: 'single'|'bulk', job?}
  const [cancelReasons, setCancelReasons] = useState([]);
  const showToast = useToast();

  useEffect(() => {
    if (
      (!prevProps?.jobs || prevProps?.jobs.length === 0) &&
      jobs?.length > 0
    ) {
      setTimeout(() => {
        setRenderColor(true);
      }, 200);
    }
    // prevProps is the render-lagged snapshot from usePrevious; the effect is
    // meant to fire only when `jobs` changes to detect the first-load transition.
    // Adding prevProps?.jobs would re-run it every render and alter that timing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs]);

  useEffect(() => {
    getRequest("/printCancelReasons", isDev)
      .then((res) => {
        const payload = res?.data?.result ?? res?.data ?? [];
        setCancelReasons(Array.isArray(payload) ? payload : []);
      })
      .catch((e) => console.error("[Printer] load cancel reasons", e));
  }, [isDev]);

  const statusCounts = useMemo(() => {
    const counts = {};
    (jobs || []).forEach((j) => {
      counts[j.status] = (counts[j.status] || 0) + 1;
    });
    return counts;
  }, [jobs]);

  const filteredJobs = useMemo(() => {
    let list = jobs || [];
    if (statusFilter) {
      const group = STATUS_FILTER_GROUPS.find((g) => g.key === statusFilter);
      const statuses = group?.statuses || [statusFilter];
      list = list.filter((j) => statuses.includes(j.status));
    }
    if (search.trim()) {
      const query = search.toLowerCase().trim();
      list = list.filter((j) =>
        [j.title, j.userEmail, j.userName, j.roomName]
          .filter(Boolean)
          .some((field) => field.toLowerCase().includes(query))
      );
    }
    return [...list].sort((a, b) => {
      const diff = (a.unix?.getTime?.() || 0) - (b.unix?.getTime?.() || 0);
      return sortAsc ? diff : -diff;
    });
  }, [jobs, statusFilter, search, sortAsc]);

  // Every loaded job is rendered — a 90-day history for the busiest printer is
  // ~1.7K cards, which the browser handles fine. What matters is that a live
  // socket merge (a new `jobs` array every time) re-renders only the card that
  // changed, so JobCard is memoized and the callbacks handed to it must keep a
  // stable identity across renders.
  const onToggleCheck = useCallback((id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const clearSelection = () => setSelectedIds(new Set());

  const runCancel = async (jobIds, balanceReasonId) => {
    const jobsById = new Map((jobs || []).map((j) => [j.id, j]));
    const targets = getCancelableJobIds(jobIds, jobsById);
    let failures = 0;
    for (const id of targets) {
      try {
        await cancelJob({ jobSelected: jobsById.get(id), balanceReasonId, isDev });
      } catch (e) {
        console.error("[Printer] cancel failed", e);
        failures += 1;
      }
    }
    if (failures) {
      showToast(`${targets.length - failures}/${targets.length} jobs cancelled — ${failures} failed.`, "error");
    } else {
      showToast(`Cancelled ${targets.length} job${targets.length === 1 ? "" : "s"}.`, "success");
    }
  };

  const viewProps = {
    jobs: filteredJobs,
    printers,
    jobSelected: drawerJob,
    onJobClick: setDrawerJob,
    renderColor,
    selectedIds,
    onToggleCheck,
    onOpenPrinter,
  };

  return (
    <Wrapper>
      <JobListHeader
        totalCount={(jobs || []).length}
        matchingCount={filteredJobs.length}
        isFiltered={!!statusFilter || !!search.trim()}
        statusCounts={statusCounts}
        search={search}
        onSearchChange={setSearch}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        sortAsc={sortAsc}
        onToggleSort={() => setSortAsc((prev) => !prev)}
        layout={layout}
        onLayoutChange={setLayout}
        title={title}
        dateRange={dateRange}
        onDateRangeChange={onDateRangeChange}
        pageLimitReached={
          !!dateRange && (jobs || []).length >= getJobsLimitForDateRange(dateRange)
        }
      />

      <Body>
        {loading ? (
          <LoaderWrap>
            <Loader type="TailSpin" color={theme.primary} height={48} width={48} />
          </LoaderWrap>
        ) : layout === "grouped" ? (
          <JobGroupedView {...viewProps} />
        ) : (
          <JobTableView {...viewProps} />
        )}
      </Body>

      <BulkActionBar
        count={selectedIds.size}
        onClear={clearSelection}
        onCancel={() => setCancelTarget({ type: "bulk" })}
      />

      {drawerJob ? (
        <JobDetailDrawer
          job={drawerJob}
          printers={printers}
          onClose={() => setDrawerJob(null)}
          onCancel={(job) => setCancelTarget({ type: "single", job })}
        />
      ) : null}

      <CancelReasonModal
        open={!!cancelTarget}
        reasons={cancelReasons}
        title={cancelTarget?.type === "bulk" ? `Cancel ${selectedIds.size} print jobs` : "Cancel print job"}
        onClose={() => setCancelTarget(null)}
        onConfirm={async (balanceReasonId) => {
          try {
            if (cancelTarget?.type === "bulk") {
              await runCancel(Array.from(selectedIds), balanceReasonId);
              clearSelection();
            } else if (cancelTarget?.job) {
              await runCancel([cancelTarget.job.id], balanceReasonId);
              setDrawerJob(null);
            }
            setCancelTarget(null);
          } catch (e) {
            console.error(e);
            showToast(e?.response?.data?.error || e.message, "error");
          }
        }}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  font-family: ${theme.fontFamily};
`;

const Body = styled.div`
  padding: 4px 24px 24px;
`;

const LoaderWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
`;

export { PrinterJobs };
